import { videoCompositionSettings } from './videoComposition.ts';
import type { TimedVideoCaption } from './videoTimeline.ts';

const graphemes = new Intl.Segmenter(undefined, { granularity: 'grapheme' });

export function splitSubtitleText(text: string, maxChars: number) {
  const characters = [...graphemes.segment(text)].map(item => item.segment);
  const parts = [];
  let start = 0;
  while (start < characters.length) {
    let end = Math.min(start + maxChars, characters.length);
    if (end < characters.length) {
      const minimum = start + Math.floor(maxChars / 2);
      let wordBoundary = 0;
      for (let cursor = end; cursor > minimum; cursor--) {
        if (/[，。！？；：、,.!?;:]/u.test(characters[cursor - 1])) { end = cursor; wordBoundary = 0; break; }
        if (!wordBoundary && /\s/u.test(characters[cursor - 1])) wordBoundary = cursor;
      }
      if (wordBoundary) end = wordBoundary;
      // Keep closing punctuation with at least one preceding character.
      while (end > start + 1 && /^[，。！？；：、,.!?;:）】》」』”’]/u.test(characters[end])) end--;
    }
    parts.push(characters.slice(start, end).join(''));
    start = end;
  }
  return parts;
}

export function subtitleCues(captions: TimedVideoCaption[], options?: unknown) {
  const settings = videoCompositionSettings(options);
  return captions.flatMap(caption => {
    const text = String(caption.text).replaceAll('\r', '').replace(/\n\s*\n/g, '\n');
    let parts = settings.split_subtitles ? splitSubtitleText(text, settings.subtitle_max_chars) : [text];
    if (!parts.length) return [];
    const startMs = Math.round(caption.startSeconds * 1000);
    const endMs = Math.round(caption.endSeconds * 1000);
    const spokenMs = Math.min(endMs - startMs, Math.round((caption.durationSeconds ?? caption.endSeconds - caption.startSeconds) * 1000));
    if (endMs <= startMs || spokenMs <= 0) throw new Error('字幕时间范围无效');
    // Extremely short source cues can hold only one positive millisecond per part.
    if (parts.length > spokenMs) {
      const grouped = Array.from({ length: spokenMs }, () => '');
      parts.forEach((part, index) => { grouped[Math.floor(index * spokenMs / parts.length)] += part; });
      parts = grouped;
    }
    const lengths = parts.map(part => [...graphemes.segment(part)].length);
    const total = lengths.reduce((sum, length) => sum + length, 0);
    let consumed = 0;
    let cursor = startMs;
    return parts.map((part, index) => {
      consumed += lengths[index];
      const end = index === parts.length - 1 ? endMs : Math.min(startMs + spokenMs - (parts.length - index - 1), Math.max(cursor + 1, startMs + Math.round(spokenMs * consumed / total)));
      const cue = { text: part, startSeconds: cursor / 1000, endSeconds: end / 1000 };
      cursor = end;
      return cue;
    });
  });
}
